/**
 * PURPOSE: Modal "Accesos" de una institución: cuentas de delegado del portal (FTR-0013 + FTR-0017
 *   Phase 4: cuentas SISDEU dentro de la misma pantalla).
 * RESPONSIBILITIES: Listar las cuentas de la institución (badges LOCAL/SISDEU, vínculo bloqueado, origen
 *   de capacidades); crear cuentas locales (capacidades desmarcadas, contraseña escrita o generada y
 *   copiable solo antes de guardar); editar datos y capacidades; restablecer contraseña; desactivar
 *   (motivo opcional) y reactivar; revocar sesiones. Para cuentas SISDEU: usuario/contraseña de solo
 *   lectura (los administra SISDEU), metadatos externos seguros visibles, botón para restablecer las
 *   capacidades a la política vigente cuando hay una excepción individual, y re-vínculo explícito cuando
 *   el identity quedó bloqueado por un cambio de institución en SISDEU. Confirmaciones con
 *   window.confirm. La contraseña se vacía al guardar y nunca vuelve a mostrarse.
 * COLLABORATORS: portalAccountService.js, portalAccounts.utils.js, sisdeuAdminService.js
 *   (vínculos aprobados para el selector de re-vínculo), sisdeuAdmin.utils.js, CompanyInstitutionsTab.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/InstitutionAccessModal.jsx
 */
import React, { useCallback, useEffect, useReducer, useState } from "react";
import toast from "react-hot-toast";

import {
  createPortalAccount,
  deactivatePortalAccount,
  listPortalAccounts,
  reactivatePortalAccount,
  relinkPortalAccountIdentity,
  resetPortalAccountCapabilitiesToPolicy,
  resetPortalAccountPassword,
  revokePortalAccountSessions,
  updatePortalAccount,
} from "../../services/portalAccountService.js";
import { listInstitutionLinks } from "../../services/sisdeuAdminService.js";
import {
  PORTAL_CAPABILITY_LABELS,
  PORTAL_CAPABILITY_OPTIONS,
  SISDEU_EMPTY_OVERRIDE_MESSAGE,
  accountFormReducer,
  buildCreatePayload,
  buildUpdatePayload,
  generatePortalPassword,
  getAccountLoginMethodLabel,
  getAccountStatusLabel,
  getCapabilitiesModeLabel,
  getIdentityStatusLabel,
  getPortalAccountErrorMessage,
  isIdentityBlocked,
  isPortalUnavailableError,
  isSisdeuAccount,
  normalizePortalUsername,
  suggestPortalUsername,
  validateAccountForm,
  validatePortalPassword,
  wouldLeaveSisdeuAccountWithEmptyOverride,
} from "./portalAccounts.utils.js";
import { getSisdeuAdminErrorMessage } from "./sisdeuAdmin.utils.js";

const INPUT_CLASS =
  "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200";
const SECONDARY_BUTTON_CLASS =
  "rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50";
const initialForm = () => accountFormReducer(undefined, { type: "reset" });

const FieldError = ({ message }) =>
  message ? <p className="mt-1 text-xs font-medium text-red-600">{message}</p> : null;

const StatusBadge = ({ status }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
      status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
    }`}
  >
    {getAccountStatusLabel(status)}
  </span>
);

// FTR-0017 Phase 4: distinguir cuentas SISDEU sin tocar el badge de estado local ya existente.
const LoginMethodBadge = ({ loginMethod }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
      loginMethod === "SISDEU" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-600"
    }`}
  >
    {getAccountLoginMethodLabel(loginMethod)}
  </span>
);

const IdentityLinkBadge = ({ identity }) =>
  isIdentityBlocked(identity) ? (
    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
      {getIdentityStatusLabel(identity)}
    </span>
  ) : null;

const CapabilitiesModeBadge = ({ capabilitiesMode }) => (
  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
    {getCapabilitiesModeLabel(capabilitiesMode)}
  </span>
);

const copyPassword = async (value) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success("Contraseña copiada.");
  } catch {
    toast.error("No se pudo copiar. Cópiala manualmente.");
  }
};

const PasswordInput = ({ id, label, value, onChange, error, disabled }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-slate-700">
      {label}
    </label>
    <div className="mt-1 flex gap-2">
      <input
        id={id}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        className={`${INPUT_CLASS} mt-0 font-mono`}
      />
      <button type="button" onClick={() => onChange(generatePortalPassword())} disabled={disabled} className={SECONDARY_BUTTON_CLASS}>
        Generar
      </button>
      <button type="button" onClick={() => copyPassword(value)} disabled={disabled || !value} className={SECONDARY_BUTTON_CLASS}>
        Copiar
      </button>
    </div>
    <p className="mt-1 text-xs text-slate-500">
      Mínimo 4 caracteres. Cópiala y compártela antes de guardar: después no se podrá volver a ver.
    </p>
    <FieldError message={error} />
  </div>
);

const SISDEU_FIELD_NOTE =
  "SISDEU administra la identidad de esta cuenta: usuario y contraseña no se pueden editar ni restablecer aquí.";

const IdentityMetadata = ({ identity }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
    <p className="font-semibold text-slate-700">Identidad externa (SISDEU)</p>
    <dl className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
      <div>
        <dt className="text-xs text-slate-500">Usuario externo</dt>
        <dd className="font-mono text-slate-700">{identity?.externalUsername || "—"}</dd>
      </div>
      <div>
        <dt className="text-xs text-slate-500">Id de cuenta externa</dt>
        <dd className="text-slate-700">{identity?.externalAccountId ?? "—"}</dd>
      </div>
      <div>
        <dt className="text-xs text-slate-500">Id de institución externa</dt>
        <dd className="text-slate-700">{identity?.externalInstitutionId ?? "—"}</dd>
      </div>
      <div>
        <dt className="text-xs text-slate-500">Última verificación</dt>
        <dd className="text-slate-700">
          {identity?.lastVerifiedAt ? new Date(identity.lastVerifiedAt).toLocaleString() : "Nunca"}
        </dd>
      </div>
    </dl>
    <p className="mt-2 text-xs text-slate-500">
      Solo metadatos seguros. Nunca se muestran ni se solicitan contraseñas, documentos ni datos de contacto externos.
    </p>
  </div>
);

const AccountFields = ({
  form,
  dispatch,
  errors,
  institution,
  account,
  disabled,
  capabilitiesMode,
  onResetCapabilitiesToPolicy,
  resettingCapabilities,
}) => {
  const change = (field) => (event) => dispatch({ type: "change", field, value: event.target.value });
  const usernameChanged = account && normalizePortalUsername(form.username) !== account.username;
  const sisdeu = isSisdeuAccount(account);

  return (
    <>
      <label className="block text-sm font-medium text-slate-700">
        Nombre del delegado *
        <input value={form.displayName} onChange={change("displayName")} disabled={disabled} className={INPUT_CLASS} />
        <FieldError message={errors.displayName} />
      </label>

      {sisdeu ? (
        <div>
          <label htmlFor="portal-account-username" className="block text-sm font-medium text-slate-700">
            Usuario
          </label>
          <input
            id="portal-account-username"
            value={form.username}
            disabled
            readOnly
            className={`${INPUT_CLASS} mt-1 cursor-not-allowed bg-slate-100 font-mono text-slate-500`}
          />
          <p className="mt-1 text-xs font-medium text-slate-500">{SISDEU_FIELD_NOTE}</p>
        </div>
      ) : (
        <div>
          <label htmlFor="portal-account-username" className="block text-sm font-medium text-slate-700">
            Usuario *
          </label>
          <div className="mt-1 flex gap-2">
            <input
              id="portal-account-username"
              value={form.username}
              onChange={change("username")}
              autoComplete="off"
              spellCheck={false}
              disabled={disabled}
              className={`${INPUT_CLASS} mt-0`}
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                const suggestion = suggestPortalUsername({ institution, displayName: form.displayName });
                if (suggestion) dispatch({ type: "change", field: "username", value: suggestion });
                else toast.error("Escribe el nombre del delegado para sugerir un usuario.");
              }}
              className={SECONDARY_BUTTON_CLASS}
            >
              Sugerir
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            3 a 64 caracteres: minúsculas, números, punto, guion o guion bajo. Debe ser único.
          </p>
          {usernameChanged && (
            <p className="mt-1 text-xs font-medium text-amber-700">
              Cambiar el usuario cierra las sesiones activas del delegado.
            </p>
          )}
          <FieldError message={errors.username} />
        </div>
      )}

      {sisdeu && <IdentityMetadata identity={account.identity} />}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm font-medium text-slate-700">
          Correo
          <input type="email" value={form.email} onChange={change("email")} disabled={disabled} className={INPUT_CLASS} />
          <FieldError message={errors.email} />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          Teléfono
          <input value={form.phone} onChange={change("phone")} disabled={disabled} className={INPUT_CLASS} />
        </label>
      </div>

      <fieldset className="rounded-lg border border-slate-200 p-3">
        <legend className="flex flex-wrap items-center gap-2 px-1 text-sm font-medium text-slate-700">
          Capacidades
          {sisdeu && <CapabilitiesModeBadge capabilitiesMode={capabilitiesMode} />}
        </legend>
        <div className="flex flex-wrap gap-4">
          {PORTAL_CAPABILITY_OPTIONS.map((option) => (
            <label key={option.value} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.capabilities.includes(option.value)}
                onChange={() => dispatch({ type: "toggleCapability", capability: option.value })}
                disabled={disabled}
              />
              {option.label}
            </label>
          ))}
        </div>
        {sisdeu ? (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              {capabilitiesMode === "POLICY"
                ? "Siguen la política de capacidades SISDEU de la compañía."
                : "Excepción individual: no cambia si la política de la compañía se modifica."}
            </p>
            {capabilitiesMode === "MANUAL" && (
              <button
                type="button"
                onClick={onResetCapabilitiesToPolicy}
                disabled={disabled || resettingCapabilities}
                className={SECONDARY_BUTTON_CLASS}
              >
                {resettingCapabilities ? "Restableciendo..." : "Restablecer a la política"}
              </button>
            )}
          </div>
        ) : (
          <p className="mt-2 text-xs text-slate-500">
            Se asignan ahora; el portal las usará cuando habilite la gestión de uniformes y alineaciones.
          </p>
        )}
      </fieldset>
    </>
  );
};

const InstitutionAccessModal = ({ institution, onClose, focusAccountId = null }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailableMessage, setUnavailableMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [form, dispatch] = useReducer(accountFormReducer, undefined, initialForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [reason, setReason] = useState("");
  // FTR-0017 Phase 4: restablecer capacidades a la política y re-vincular una identidad bloqueada.
  const [resettingCapabilities, setResettingCapabilities] = useState(false);
  const [linkedInstitutions, setLinkedInstitutions] = useState([]);
  const [loadingLinkedInstitutions, setLoadingLinkedInstitutions] = useState(false);
  const [relinkTargetId, setRelinkTargetId] = useState("");
  const [relinking, setRelinking] = useState(false);
  const [focusedAccountId, setFocusedAccountId] = useState(null);

  const selected = accounts.find((account) => account.id === selectedId) || null;

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listPortalAccounts(institution.idinstitution);
      setAccounts(list);
      setUnavailableMessage("");
      return list;
    } catch (error) {
      if (isPortalUnavailableError(error)) {
        setUnavailableMessage(getPortalAccountErrorMessage(error, ""));
      } else {
        toast.error(getPortalAccountErrorMessage(error, "No se pudieron cargar los accesos."));
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [institution.idinstitution]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Navegación desde una alerta SISDEU (FTR-0017 §7): abre directo en la cuenta afectada, una sola vez.
  useEffect(() => {
    if (!focusAccountId || focusedAccountId === focusAccountId || accounts.length === 0) return;
    const account = accounts.find((item) => item.id === focusAccountId);
    if (account) {
      setFocusedAccountId(focusAccountId);
      startManage(account);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, focusAccountId, focusedAccountId]);

  const clearSecondaryInputs = () => {
    setErrors({});
    setResetPassword("");
    setReason("");
    setRelinkTargetId("");
  };

  const startCreate = () => {
    setSelectedId(null);
    dispatch({ type: "reset" });
    clearSecondaryInputs();
  };

  const startManage = (account) => {
    setSelectedId(account.id);
    dispatch({ type: "load", account });
    clearSecondaryInputs();
  };

  const replaceAccount = (account) =>
    setAccounts((current) => current.map((item) => (item.id === account.id ? account : item)));

  const handleCreate = async (event) => {
    event.preventDefault();
    const nextErrors = validateAccountForm(form, { requirePassword: true });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const account = await createPortalAccount(buildCreatePayload(form, institution.idinstitution));
      // Guardada: la contraseña deja de existir en la interfaz.
      dispatch({ type: "reset" });
      toast.success(`Cuenta ${account.username} creada.`);
      const list = await loadAccounts();
      const created = list.find((item) => item.id === account.id);
      if (created) startManage(created);
    } catch (error) {
      const message = getPortalAccountErrorMessage(error, "No se pudo crear la cuenta.");
      if (error?.response?.data?.code === "USERNAME_TAKEN") setErrors({ username: message });
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    const nextErrors = validateAccountForm(form, { isSisdeu: isSisdeuAccount(selected) });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload = buildUpdatePayload(form, selected);
    if (!Object.keys(payload).length) {
      toast("No hay cambios para guardar.");
      return;
    }
    // Regla aprobada (FTR-0017): una excepción individual SISDEU nunca puede quedar vacía; se anticipa
    // al 400 del backend para no gastar un viaje de red en un cambio que ya sabemos que será rechazado.
    if (payload.capabilities && wouldLeaveSisdeuAccountWithEmptyOverride(selected, payload.capabilities)) {
      toast.error(SISDEU_EMPTY_OVERRIDE_MESSAGE);
      return;
    }
    if (
      payload.username &&
      !window.confirm(
        `¿Cambiar el usuario a ${payload.username}? Se cerrarán las sesiones activas del delegado.`
      )
    ) {
      return;
    }

    setSaving(true);
    try {
      const account = await updatePortalAccount(selected.id, payload);
      replaceAccount(account);
      dispatch({ type: "load", account });
      toast.success("Cuenta actualizada.");
    } catch (error) {
      const message = getPortalAccountErrorMessage(error, "No se pudo actualizar la cuenta.");
      if (error?.response?.data?.code === "USERNAME_TAKEN") setErrors({ username: message });
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Acciones de ciclo de vida: no tocan el formulario de datos (pueden quedar ediciones sin guardar).
  const runAction = async ({ confirmText, action, success, fallback, after }) => {
    if (!window.confirm(confirmText)) return;
    setSaving(true);
    try {
      replaceAccount(await action());
      after?.();
      toast.success(success);
    } catch (error) {
      toast.error(getPortalAccountErrorMessage(error, fallback));
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = (event) => {
    event.preventDefault();
    const passwordError = validatePortalPassword(resetPassword);
    setErrors(passwordError ? { resetPassword: passwordError } : {});
    if (passwordError) return;
    runAction({
      confirmText: `¿Restablecer la contraseña de ${selected.username}? Se cerrarán sus sesiones activas.`,
      action: () => resetPortalAccountPassword(selected.id, resetPassword),
      success: "Contraseña restablecida.",
      fallback: "No se pudo restablecer la contraseña.",
      after: () => setResetPassword(""),
    });
  };

  const handleDeactivate = () =>
    runAction({
      confirmText: `¿Desactivar la cuenta ${selected.username}? No podrá ingresar y se cerrarán sus sesiones.`,
      action: () => deactivatePortalAccount(selected.id, reason),
      success: "Cuenta desactivada.",
      fallback: "No se pudo desactivar la cuenta.",
      after: () => setReason(""),
    });

  const handleReactivate = () =>
    runAction({
      confirmText: `¿Reactivar la cuenta ${selected.username}? Podrá ingresar con su contraseña actual.`,
      action: () => reactivatePortalAccount(selected.id),
      success: "Cuenta reactivada.",
      fallback: "No se pudo reactivar la cuenta.",
    });

  const handleRevokeSessions = () =>
    runAction({
      confirmText: `¿Cerrar todas las sesiones activas de ${selected.username}?`,
      action: () => revokePortalAccountSessions(selected.id),
      success: "Sesiones revocadas.",
      fallback: "No se pudieron revocar las sesiones.",
    });

  // FTR-0017 Phase 4: quitar la excepción individual y volver a la política vigente de la compañía.
  const handleResetCapabilitiesToPolicy = async () => {
    if (!selected) return;
    if (!window.confirm(`¿Restablecer las capacidades de ${selected.username} a la política de la compañía?`)) return;
    setResettingCapabilities(true);
    try {
      const account = await resetPortalAccountCapabilitiesToPolicy(selected.id);
      replaceAccount(account);
      dispatch({ type: "load", account });
      toast.success("Capacidades restablecidas a la política.");
    } catch (error) {
      toast.error(getPortalAccountErrorMessage(error, "No se pudieron restablecer las capacidades."));
    } finally {
      setResettingCapabilities(false);
    }
  };

  // Vínculos SISDEU aprobados de la compañía, para elegir la institución destino del re-vínculo. Se
  // cargan solo cuando hacen falta (identidad bloqueada), no en cada apertura del modal.
  const loadLinkedInstitutions = useCallback(async () => {
    setLoadingLinkedInstitutions(true);
    try {
      setLinkedInstitutions(await listInstitutionLinks());
    } catch (error) {
      toast.error(getSisdeuAdminErrorMessage(error, "No se pudieron cargar los vínculos SISDEU."));
    } finally {
      setLoadingLinkedInstitutions(false);
    }
  }, []);

  useEffect(() => {
    if (selected && isIdentityBlocked(selected.identity) && linkedInstitutions.length === 0 && !loadingLinkedInstitutions) {
      loadLinkedInstitutions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, selected?.identity?.status]);

  const handleRelink = async () => {
    if (!selected || !relinkTargetId) return;
    const target = linkedInstitutions.find((link) => String(link.institution?.id) === String(relinkTargetId));
    if (
      !window.confirm(
        `¿Re-vincular ${selected.username} a ${target?.institution?.name || "la institución elegida"}? Las capacidades se tomarán de la política vigente, nunca de la institución anterior.`
      )
    ) {
      return;
    }
    setRelinking(true);
    try {
      const account = await relinkPortalAccountIdentity(selected.id, Number(relinkTargetId));
      // La cuenta puede haber quedado en otra institución: refresca la lista de ESTA institución.
      const list = await loadAccounts();
      const stillHere = list.find((item) => item.id === account.id);
      if (stillHere) startManage(stillHere);
      else {
        setSelectedId(null);
        dispatch({ type: "reset" });
      }
      setRelinkTargetId("");
      toast.success("Cuenta re-vinculada. El acceso quedó restablecido.");
    } catch (error) {
      toast.error(getPortalAccountErrorMessage(error, "No se pudo re-vincular la cuenta."));
    } finally {
      setRelinking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onMouseDown={(mouseEvent) => mouseEvent.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="institution-access-title"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 id="institution-access-title" className="text-xl font-bold text-slate-800">
              Accesos de delegados
            </h3>
            <p className="text-sm text-slate-500">
              {institution.name}
              {institution.abrev ? ` · ${institution.abrev}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-3 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {unavailableMessage ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {unavailableMessage}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <section className="min-w-0">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h4 className="font-semibold text-slate-800">Cuentas</h4>
                <button
                  type="button"
                  onClick={startCreate}
                  className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                >
                  Nueva cuenta
                </button>
              </div>
              {loading ? (
                <div className="py-10 text-center text-sm text-slate-500">Cargando...</div>
              ) : accounts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
                  Esta institución aún no tiene cuentas de delegado.
                </div>
              ) : (
                <ul className="space-y-2">
                  {accounts.map((account) => (
                    <li key={account.id}>
                      <button
                        type="button"
                        onClick={() => startManage(account)}
                        className={`w-full rounded-lg border p-3 text-left ${
                          account.id === selectedId ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-semibold text-slate-800">{account.username}</span>
                          <StatusBadge status={account.status} />
                          <LoginMethodBadge loginMethod={account.loginMethod} />
                          <IdentityLinkBadge identity={account.identity} />
                        </div>
                        <p className="truncate text-sm text-slate-500">{account.displayName}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {account.capabilities?.length
                            ? account.capabilities.map((item) => PORTAL_CAPABILITY_LABELS[item] || item).join(" · ")
                            : "Sin capacidades"}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="min-w-0 rounded-xl border border-slate-200 p-4">
              {!selected ? (
                <form onSubmit={handleCreate} className="space-y-3" noValidate>
                  <h4 className="font-semibold text-slate-800">Nueva cuenta</h4>
                  <AccountFields
                    form={form}
                    dispatch={dispatch}
                    errors={errors}
                    institution={institution}
                    disabled={saving}
                  />
                  <PasswordInput
                    id="portal-account-password"
                    label="Contraseña *"
                    value={form.password}
                    onChange={(value) => dispatch({ type: "change", field: "password", value })}
                    error={errors.password}
                    disabled={saving}
                  />
                  <div className="flex justify-end pt-1">
                    <button
                      type="submit"
                      disabled={saving}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {saving ? "Guardando..." : "Crear cuenta"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-mono font-semibold text-slate-800">{selected.username}</h4>
                    <StatusBadge status={selected.status} />
                    <LoginMethodBadge loginMethod={selected.loginMethod} />
                    <IdentityLinkBadge identity={selected.identity} />
                  </div>

                  <form onSubmit={handleUpdate} className="space-y-3" noValidate>
                    <AccountFields
                      form={form}
                      dispatch={dispatch}
                      errors={errors}
                      institution={institution}
                      account={selected}
                      disabled={saving}
                      capabilitiesMode={selected.capabilitiesMode}
                      onResetCapabilitiesToPolicy={handleResetCapabilitiesToPolicy}
                      resettingCapabilities={resettingCapabilities}
                    />
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={saving}
                        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                      >
                        {saving ? "Guardando..." : "Guardar cambios"}
                      </button>
                    </div>
                  </form>

                  {!isSisdeuAccount(selected) && (
                    <form onSubmit={handleResetPassword} className="space-y-2 border-t border-slate-200 pt-4" noValidate>
                      <h5 className="text-sm font-semibold text-slate-800">Restablecer contraseña</h5>
                      <PasswordInput
                        id="portal-account-reset-password"
                        label="Nueva contraseña"
                        value={resetPassword}
                        onChange={setResetPassword}
                        error={errors.resetPassword}
                        disabled={saving}
                      />
                      <div className="flex justify-end">
                        <button type="submit" disabled={saving} className={SECONDARY_BUTTON_CLASS}>
                          Restablecer contraseña
                        </button>
                      </div>
                    </form>
                  )}

                  {isIdentityBlocked(selected.identity) && (
                    <div className="space-y-2 border-t border-amber-200 bg-amber-50 -mx-4 px-4 pt-4 pb-3">
                      <h5 className="text-sm font-semibold text-amber-800">Re-vincular identidad SISDEU</h5>
                      <p className="text-xs text-amber-800">
                        El vínculo quedó bloqueado por un cambio de institución en SISDEU. Elige la institución
                        destino (con vínculo SISDEU aprobado) para restablecer el acceso; las capacidades se
                        tomarán de la política vigente, nunca de la institución anterior.
                      </p>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                        <label className="block flex-1 text-sm font-medium text-amber-900">
                          Institución destino
                          <select
                            value={relinkTargetId}
                            onChange={(event) => setRelinkTargetId(event.target.value)}
                            disabled={loadingLinkedInstitutions || relinking}
                            className={`${INPUT_CLASS} mt-1`}
                          >
                            <option value="">
                              {loadingLinkedInstitutions ? "Cargando vínculos..." : "Selecciona una institución"}
                            </option>
                            {linkedInstitutions.map((link) => (
                              <option key={link.id} value={link.institution?.id}>
                                {link.institution?.name || `Institución ${link.institution?.id}`}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={handleRelink}
                          disabled={!relinkTargetId || relinking}
                          className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                        >
                          {relinking ? "Re-vinculando..." : "Re-vincular"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 border-t border-slate-200 pt-4">
                    <h5 className="text-sm font-semibold text-slate-800">Estado y sesiones</h5>
                    {selected.status === "ACTIVE" ? (
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                        <label className="block flex-1 text-sm font-medium text-slate-700">
                          Motivo de la desactivación (opcional)
                          <input
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            maxLength={255}
                            disabled={saving}
                            className={INPUT_CLASS}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={handleDeactivate}
                          disabled={saving}
                          className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Desactivar
                        </button>
                      </div>
                    ) : isIdentityBlocked(selected.identity) ? (
                      <p className="text-xs font-medium text-amber-700">
                        Re-vincula la identidad SISDEU (arriba) antes de reactivar esta cuenta.
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleReactivate}
                        disabled={saving}
                        className="rounded-lg border border-emerald-200 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        Reactivar
                      </button>
                    )}
                    <div>
                      <button type="button" onClick={handleRevokeSessions} disabled={saving} className={SECONDARY_BUTTON_CLASS}>
                        Revocar sesiones
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstitutionAccessModal;
